import type { ArchetypePassive, ElementType, TacticalRole } from '../../types';
import type { ActionCandidate, ActionType } from '../ai/ActionCandidate';
import type { PassType } from '../ai/ActionCandidate';
import type { PlayerDecisionContext } from '../ai/PlayerDecisionContext';
import type { WeightedSelectionTrace } from '../ai/WeightedActionSelector';
import type { EngineMatchEventType } from '../match/MatchEvent';
import type { Vector2 } from '../match/Vector2';
import type { PlayerStats } from '../data/EngineData';

export interface DecisionLogEntry {
  matchId: string;
  seed: number;
  minute: number;
  actorId: string;
  position: Vector2;
  element: ElementType;
  assignedRole: TacticalRole | null;
  passive: ArchetypePassive | null;
  roleBiasesApplied: string[];
  passiveModifiersApplied: string[];
  elementalModifier: number;
  debugReason: string;
  stats: PlayerStats;
  hasBall: boolean;
  fieldZone: string;
  scoreDiff: number;
  teamPhase: import('../ai/TeamAIDirector').TeamPhase;
  tacticalPlan: {
    mentality: number;
    pressing: number;
    tempo: number;
    width: number;
    defensiveLine: number;
    risk: number;
    techniqueAggression: number;
  };
  pressureLevel: number;
  nearestOpponentDistance: number;
  nearestOpponentId?: string;
  localSpace: number;
  goalAngleQuality: number;
  distanceToGoal: number;
  currentTp: number;
  stamina: number;
  consecutiveTeamPasses: number;
  recentTerritorialProgress: number;
  sequenceStartProgress?: number;
  currentAction: ActionType;
  availableActions: ActionType[];
  utilityScores: Array<{
    type: ActionType;
    targetPlayerId?: string;
    techniqueId?: string;
    passType?: PassType;
    score: number;
    rawScore: number;
    antiLoopPenalty: number;
    selectionWeight: number;
    selectionProbability: number;
    passingLane?: {
      distance: number;
      forwardGain: number;
      blockers: number;
      nearestOpponentDistance: number;
      openness: number;
      risk: number;
    };
    contributions: Array<{ key: string; value: number }>;
    roleBiasesApplied: string[];
    passiveModifiersApplied: string[];
    elementalModifier: number;
    debugReason: string;
  }>;
  selection: WeightedSelectionTrace;
  chosenAction: ActionType;
  targetPosition?: Vector2;
  targetPlayerId?: string;
  techniqueId?: string;
  passType?: PassType;
  resultEvent?: EngineMatchEventType;
}

export class DecisionLogger {
  constructor(private readonly enabled = true) {}

  record(
    context: PlayerDecisionContext,
    candidates: ActionCandidate[],
    chosen: ActionCandidate,
    selection: WeightedSelectionTrace,
  ): DecisionLogEntry | undefined {
    if (!this.enabled) return undefined;
    const chosenBreakdown = chosen.utilityBreakdown;
    const entry: DecisionLogEntry = {
      matchId: context.state.matchId,
      seed: context.state.seed,
      minute: context.minute,
      actorId: context.actor.playerId,
      position: { ...context.actor.position },
      element: context.actor.element,
      assignedRole: context.actor.positionType === 'GK' ? null : context.actor.tacticalProfile.assignedRole,
      passive: context.actor.passive,
      roleBiasesApplied: chosenBreakdown?.roleBiasesApplied ?? [],
      passiveModifiersApplied: chosenBreakdown?.passiveModifiersApplied ?? [],
      elementalModifier: chosenBreakdown?.elementalModifier ?? 1,
      debugReason: chosen.debugReason,
      stats: { ...context.actor.stats },
      hasBall: context.hasBall,
      fieldZone: context.fieldZone,
      scoreDiff: context.scoreDiff,
      teamPhase: context.teamPlan.phase,
      tacticalPlan: {
        mentality: context.teamPlan.mentality,
        pressing: context.teamPlan.pressing,
        tempo: context.teamPlan.tempo,
        width: context.teamPlan.width,
        defensiveLine: context.teamPlan.defensiveLine,
        risk: context.teamPlan.risk,
        techniqueAggression: context.teamPlan.techniqueAggression,
      },
      pressureLevel: context.pressureLevel,
      nearestOpponentDistance: context.perception.nearestOpponentDistance,
      nearestOpponentId: context.opponents
        .slice()
        .sort((first, second) => {
          const firstDistance = Math.hypot(first.position.x - context.actor.position.x, first.position.y - context.actor.position.y);
          const secondDistance = Math.hypot(second.position.x - context.actor.position.x, second.position.y - context.actor.position.y);
          return firstDistance - secondDistance || first.playerId.localeCompare(second.playerId);
        })[0]?.playerId,
      localSpace: context.perception.localSpace,
      goalAngleQuality: context.perception.goalAngleQuality,
      distanceToGoal: context.distanceToGoal,
      currentTp: context.actor.tpCurrent,
      stamina: context.actor.staminaCurrent,
      consecutiveTeamPasses: context.consecutiveTeamPasses,
      recentTerritorialProgress: context.recentTerritorialProgress,
      sequenceStartProgress: context.sequenceStartProgress,
      currentAction: context.actor.currentAction,
      availableActions: candidates.map((candidate) => candidate.type),
      utilityScores: candidates.map((candidate) => ({
        type: candidate.type,
        targetPlayerId: candidate.targetPlayerId,
        techniqueId: candidate.techniqueId,
        passType: candidate.passType,
        score: candidate.utilityScore,
        rawScore: candidate.utilityBreakdown?.rawScore ?? candidate.utilityScore,
        antiLoopPenalty: candidate.antiLoopPenalty ?? 0,
        selectionWeight: candidate.selectionWeight ?? 0,
        selectionProbability: candidate.selectionProbability ?? 0,
        passingLane: candidate.passingLane ? {
          distance: candidate.passingLane.distance,
          forwardGain: candidate.passingLane.forwardGain,
          blockers: candidate.passingLane.blockers,
          nearestOpponentDistance: candidate.passingLane.nearestOpponentDistance,
          openness: candidate.passingLane.openness,
          risk: candidate.passingLane.risk,
        } : undefined,
        contributions: candidate.utilityBreakdown?.contributions.map((contribution) => ({ ...contribution })) ?? [],
        roleBiasesApplied: candidate.utilityBreakdown?.roleBiasesApplied ?? [],
        passiveModifiersApplied: candidate.utilityBreakdown?.passiveModifiersApplied ?? [],
        elementalModifier: candidate.utilityBreakdown?.elementalModifier ?? 1,
        debugReason: candidate.debugReason,
      })),
      selection: { ...selection },
      chosenAction: chosen.type,
      targetPosition: chosen.targetPosition ? { ...chosen.targetPosition } : undefined,
      targetPlayerId: chosen.targetPlayerId,
      techniqueId: chosen.techniqueId,
      passType: chosen.passType,
    };
    context.state.decisionLogs.push(entry);
    return entry;
  }

  attachResult(entries: DecisionLogEntry[], actorId: string, resultEvent: EngineMatchEventType): void {
    const entry = [...entries].reverse().find((candidate) => candidate.actorId === actorId && candidate.resultEvent === undefined);
    if (entry) entry.resultEvent = resultEvent;
  }
}

export type DecisionTrace = DecisionLogEntry;
